// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  State,
} from "@stencil/core";

import state from "../store";
import { onMobileBreakpoint } from "../utils";

/**
 * @slot navigation - Banner content for the megamenu item.
 */

const isCounter = (value: unknown): value is number =>
  typeof value === "string"
    ? !Number.isNaN(Number.parseFloat(value))
    : typeof value === "number";

@Component({
  tag: "qds-web-megamenu-item",
  shadow: true,
  styleUrl: "megamenu-item.css",
})
export class MegamenuItem implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * Emitted when the megamenu items are closed.
   */
  @Event({ eventName: "qds-megamenu-item:close" })
  public closeMegamenuItems: EventEmitter<boolean>;

  /**
   * The megamenu item's label.
   */
  @Prop() public readonly label: string;

  /**
   *
   * The megamenu item's icon name.
   */
  @Prop() public readonly iconName?: string;

  /**
   *
   * The megamenu item's icon library.
   */
  @Prop() public readonly iconLibrary?: string;

  /**
   * When specified, the megamenu item will show a photo on the left
   */
  @Prop() public readonly photo?: string;

  /**
   * Displays a counter badge if a number or string representing a number is used.
   */
  @Prop() public readonly badge?: number | string;

  /**
   * The counter badge's accessible text that will be used by
   * screen readers.
   */
  @Prop() public readonly badgeDescription?: string;

  @State() public open: boolean;

  @State() public isCurrentContent = false;

  @State() public itemId: string;

  @State() public isMobile: boolean;

  public timeoutId: NodeJS.Timeout | undefined;

  private cleanupMobileListener?: () => void;

  get #hasPhoto(): boolean {
    return this.photo !== undefined && this.photo !== "";
  }

  get #hasIcon(): boolean {
    return this.iconName !== undefined && this.iconName !== "";
  }

  @Listen("qds-megamenu-item:close", { target: "document" })
  public handleCloseMegamenuItemsEvent(): void {
    this.open = false;
    state.item = "";
  }

  @Listen("focus")
  public handleFocusEvent(): void {
    if (!this.isMobile) {
      this.open = true;
      state.item = this.itemId;
    }
  }

  @Listen("keydown")
  public handleKeydownEvent(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.handleClick();
    }
  }

  @Listen("click")
  public handleClickEvent(): void {
    this.handleClick();
  }

  public componentDidLoad(): void {
    this.itemId = this.el.getAttribute("id") ?? "";
  }

  public handleClick(): void {
    this.closeMegamenuItems.emit();
    this.open = true;
    state.item = this.itemId;
  }

  // debounce effect
  public handleMouseEnter(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.open = true;
      state.item = this.itemId;
    }, 200);
  }

  // debounce effect
  public handleMouseLeave(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    this.open = !!(!this.isMobile && state.item === this.itemId);

    return (
      <Host
        role={this.isMobile ? undefined : "tab"}
        tabIndex={this.isMobile ? undefined : 0}
      >
        {this.isMobile ? (
          <button type="button">
            <div class="qds-mobile-title">{this.label}</div>
            <qds-icon aria-hidden="true" library="core" name="navigation" />
          </button>
        ) : (
          <div
            class={{
              "qds-wrapper": true,
              "qds-selected": this.open,
            }}
            onMouseEnter={() => {
              this.handleMouseEnter();
            }}
            onMouseLeave={() => {
              this.handleMouseLeave();
            }}
          >
            {this.#hasPhoto && <img src={this.photo} alt="img" />}

            {this.#hasIcon && (
              <qds-icon
                aria-hidden="true"
                library={this.iconLibrary}
                name={this.iconName}
              />
            )}
            <div class="qds-label">{this.label}</div>
            {isCounter(this.badge) && (
              <qds-badge-counter
                description={this.badgeDescription}
                value={this.badge}
              />
            )}
          </div>
        )}
      </Host>
    );
  }
}
